import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Button from '../components/common/Button';
import './ResetPassword.css';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : '/api')
).replace(/\/+$/, '');

const ResetPassword = () => {
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const requestInProgress = useRef(false);

  const validToken = /^[a-f0-9]{64}$/.test(token);

  const validLength =
    password.length >= 8 &&
    new TextEncoder().encode(password).length <= 72;

  const passwordsMatch =
    password.length > 0 &&
    password === confirmPassword;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (requestInProgress.current) {
      return;
    }

    setError('');

    if (!validToken) {
      setError('This reset link is invalid. Please request a new one.');
      return;
    }

    if (!validLength) {
      setError(
        'Use at least 8 characters and no more than 72 UTF-8 bytes.'
      );
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    requestInProgress.current = true;
    setLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          token,
          password,
          confirmPassword
        }),
        signal: controller.signal
      });

      const data = await response.json().catch(() => null);

      if (response.status === 404 || response.status === 405) {
        throw new Error(
          'The password reset service is unavailable. Please try again later.'
        );
      }

      if (response.status === 429) {
        throw new Error(
          'Too many reset attempts. Please try again in 15 minutes.'
        );
      }

      if (!response.ok || data?.success !== true) {
        throw new Error(
          typeof data?.message === 'string'
            ? data.message
            : 'Unable to reset your password. Please try again.'
        );
      }

      setPassword('');
      setConfirmPassword('');
      setSubmitted(true);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError(
          'The request timed out. Try logging in with your new password before requesting another reset link.'
        );
      } else if (err instanceof TypeError) {
        setError(
          'Unable to connect to the server. Please check your connection.'
        );
      } else {
        setError(err.message || 'Unable to reset your password.');
      }
    } finally {
      clearTimeout(timeoutId);
      requestInProgress.current = false;
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="reset-password-page">
        <div className="reset-container">
          <div className="success-icon">
            <i
              className="fas fa-check-circle"
              aria-hidden="true"
            ></i>
          </div>

          <h1>Password reset successfully!</h1>

          <p className="subtitle" role="status">
            Your password has been changed. Log in using your new password.
          </p>

          <Link to="/login" className="login-link">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="reset-password-page">
        <div className="reset-container">
          <h1>Invalid reset link</h1>

          <p className="subtitle">
            Open the complete link from your reset email or request a new link.
          </p>

          <Link to="/forgot-password" className="login-link">
            Request a New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-container" aria-busy={loading}>
        <h1>Create New Password</h1>

        <p className="subtitle">
          Choose a new password for your SeaZex account.
        </p>

        {error && (
          <div className="error-alert" role="alert">
            <i
              className="fas fa-exclamation-circle"
              aria-hidden="true"
            ></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="new-password">New Password</label>

            <input
              id="new-password"
              name="newPassword"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your new password"
              autoComplete="new-password"
              minLength={8}
              maxLength={72}
              disabled={loading}
              required
            />
          </div>

          <div className="password-requirements">
            <p>Password requirements:</p>

            <ul>
              <li className={validLength ? 'valid' : ''}>
                <i
                  className={`fas ${
                    validLength ? 'fa-check-circle' : 'fa-circle'
                  }`}
                  aria-hidden="true"
                ></i>
                At least 8 characters, up to 72 UTF-8 bytes
              </li>

              <li className={passwordsMatch ? 'valid' : ''}>
                <i
                  className={`fas ${
                    passwordsMatch ? 'fa-check-circle' : 'fa-circle'
                  }`}
                  aria-hidden="true"
                ></i>
                Both passwords must match
              </li>
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">
              Confirm New Password
            </label>

            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(event) =>
                setConfirmPassword(event.target.value)
              }
              placeholder="Confirm your new password"
              autoComplete="new-password"
              minLength={8}
              maxLength={72}
              disabled={loading}
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading && (
              <i
                className="fas fa-spinner fa-spin"
                aria-hidden="true"
              ></i>
            )}
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="subtitle">
          <Link to="/forgot-password">
            Request a new reset link
          </Link>
        </p>

        <Link to="/login" className="back-link">
          <i className="fas fa-arrow-left" aria-hidden="true"></i>{' '}
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;